/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import type { AnyAction } from '@reduxjs/toolkit';

import type { RecommendationsSlice } from '../../../../ts/state';
import * as actionType from './actionTypes';

const initialState: RecommendationsSlice = {
  entities: {
    existingValueLoading: false,
    existingValueSynonyms: [],
    existingValue: '',
    existingValueError: null,
    newValueLoading: false,
    newValueSynonyms: [],
    newValue: null,
    newValueError: null
  },
  recommendation_sources: {
    loading: false,
    sources: [],
    error: null,
    setLoading: false,
    setStatus: '',
    setError: null,
    recommendationSourcesOpen: false,
    fromComponentToManageSources: '',
    fromComponentToSelectAssistant: '',
    rsAssistantListOpen: false,
    migrated: false,
    initialCall: false
  }
};

const getExistingValueRecommendationsStart = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  entities: {
    ...state.entities,
    existingValueLoading: true,
    existingValueSynonyms: [],
    existingValue: action.valueId,
    existingValueError: null
  }
});

const getExistingValueRecommendationsSuccess = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  entities: {
    ...state.entities,
    existingValueLoading: false,
    existingValueSynonyms: action.newRecommendations
  }
});

const getExistingValueRecommendationsError = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  entities: {
    ...state.entities,
    existingValueLoading: false,
    existingValueSynonyms: [],
    existingValueError: action.error
  }
});

const getNewValueRecommendationsStart = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  entities: {
    ...state.entities,
    newValueLoading: true,
    newValueSynonyms: [],
    newValue: action.valueId,
    newValueError: null
  }
});

const getNewValueRecommendationsSuccess = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  entities: {
    ...state.entities,
    newValueLoading: false,
    newValueSynonyms: action.newRecommendations
  }
});

const getNewValueRecommendationsError = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  entities: {
    ...state.entities,
    newValueLoading: false,
    newValueSynonyms: [],
    newValueError: action.error
  }
});

const listRecommendationSourcesStart = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  recommendation_sources: {
    ...state.recommendation_sources,
    loading: true,
    error: null,
    initialCall: action.initialCall
  }
});

const listRecommendationSourcesSuccess = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  recommendation_sources: {
    ...state.recommendation_sources,
    loading: false,
    sources: action.sources,
    migrated: action.migrated,
    error: null
  }
});

const listRecommendationSourcesError = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  recommendation_sources: {
    ...state.recommendation_sources,
    loading: false,
    sources: [],
    migrated: false,
    error: action.error
  }
});

const setRecommendationSourcesStart = (
  state: RecommendationsSlice
): RecommendationsSlice => ({
  ...state,
  recommendation_sources: {
    ...state.recommendation_sources,
    setLoading: true,
    setError: null,
    setStatus: ''
  }
});

const setRecommendationSourcesSuccess = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  recommendation_sources: {
    ...state.recommendation_sources,
    sources: action.sources,
    setLoading: false,
    setError: null,
    setStatus: action.status
  }
});

const setRecommendationSourcesError = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  recommendation_sources: {
    ...state.recommendation_sources,
    setLoading: false,
    setError: action.error,
    setStatus: action.status
  }
});

const toggleRecommendationSources = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  recommendation_sources: {
    ...state.recommendation_sources,
    recommendationSourcesOpen:
      !state.recommendation_sources.recommendationSourcesOpen,
    fromComponentToManageSources: action.fromComponent
  }
});

const toggleRecSourceAssistants = (
  state: RecommendationsSlice,
  action: AnyAction
): RecommendationsSlice => ({
  ...state,
  recommendation_sources: {
    ...state.recommendation_sources,
    rsAssistantListOpen: !state.recommendation_sources.rsAssistantListOpen,
    fromComponentToSelectAssistant: action.fromComponent
  }
});

const toggleFileUploader = (
  state: RecommendationsSlice
): RecommendationsSlice => ({
  ...state
});

export const reducer = (
  state: RecommendationsSlice = initialState,
  action: AnyAction
): RecommendationsSlice => {
  switch (action.type) {
    case actionType.FETCH_RECOMMENDATION_SOURCES_START:
      return listRecommendationSourcesStart(state, action);
    case actionType.FETCH_RECOMMENDATION_SOURCES_SUCCESS:
      return listRecommendationSourcesSuccess(state, action);
    case actionType.FETCH_RECOMMENDATION_SOURCES_ERROR:
      return listRecommendationSourcesError(state, action);
    case actionType.SET_RECOMMENDATION_SOURCES_START:
      return setRecommendationSourcesStart(state);
    case actionType.SET_RECOMMENDATION_SOURCES_SUCCESS:
      return setRecommendationSourcesSuccess(state, action);
    case actionType.SET_RECOMMENDATION_SOURCES_ERROR:
      return setRecommendationSourcesError(state, action);
    case actionType.FETCH_RECOMMENDATIONS_EXISTING_VALUE:
      return getExistingValueRecommendationsStart(state, action);
    case actionType.ADD_RECOMMENDATIONS_EXISTING_VALUE:
      return getExistingValueRecommendationsSuccess(state, action);
    case actionType.FETCH_RECOMMENDATIONS_ERROR_EXISTING_VALUE:
      return getExistingValueRecommendationsError(state, action);
    case actionType.FETCH_RECOMMENDATIONS_NEW_VALUE:
      return getNewValueRecommendationsStart(state, action);
    case actionType.ADD_RECOMMENDATIONS_NEW_VALUE:
      return getNewValueRecommendationsSuccess(state, action);
    case actionType.FETCH_RECOMMENDATIONS_ERROR_NEW_VALUE:
      return getNewValueRecommendationsError(state, action);
    case actionType.TOGGLE_RECOMMENDATION_SOURCES:
      return toggleRecommendationSources(state, action);
    case actionType.TOGGLE_REC_SOURCE_ASSISTANTS:
      return toggleRecSourceAssistants(state, action);
    case actionType.TOGGLE_FILE_UPLOADER:
      return toggleFileUploader(state);
    default:
      return state;
  }
};

export default reducer;
